import { Badge, Box, Button, DrawerBody, DrawerFooter, FormControl, FormHelperText, FormLabel, Icon, Input, Select, Stack, Switch, useToast } from "@chakra-ui/react";
import ContentSelection from "components/contentSelection/contentSelection";
import { DrawerContext } from "components/drawer";
import GroupForm from "components/groupForm/groupForm";
import { AuthContext } from "contexts/AuthContext";
import { useBroadcast } from "hooks/useBroadcast";
import { useGroup } from "hooks/useGroup";
import { useLocation } from "hooks/useLocation";
import { create, createBroadcastRecord, save } from "lib/firebase";
import { groupTemplate } from "lib/membership";
import moment from "moment";
import { useContext, useEffect, useState } from "react";
import { ChromePicker } from "react-color";
import { MdDone, MdWarningAmber } from "react-icons/md";

// `group` edits an existing group. `template` (a group to copy, see lib/membership.js#groupTemplate)
// opens the CREATE form with all of its values filled in and only the name left empty.
export default function GroupModal({ group, template, onChange }) {
  const [data, setData] = useState(() => template ? groupTemplate(template) : group)
  const { user } = useContext(AuthContext)

  const { closeDrawer } = useContext(DrawerContext)
  const toast = useToast()

  // Only another group replaces the form. The lists follow the database live, so the same group
  // arrives again as a new object whenever anything changes: that must not wipe what is being typed.
  useEffect(() => {
    if (group) {
      setData(group || {})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group?.id])

  // Creates the broadcast chosen in the form and returns it (with its new id).
  // Errors propagate to the caller, which reports them in its own try/catch.
  const createBroadcast = async (broadcast) => {
    return createBroadcastRecord({
      ...broadcast,
      clientId: user.clientId,
      updated_at: moment().toISOString()
    })
  }

  // Builds the record to store: resolves a "new broadcast" selection into a real
  // broadcastId and strips the denormalised `broadcast` object. Works on a copy,
  // so the form state stays intact if saving fails.
  const buildPayload = async () => {
    const { broadcast, ...payload } = data
    if (payload.broadcastId?.startsWith('new')) {
      const created = await createBroadcast(broadcast)
      payload.broadcastId = created.id
    }
    payload.updated_at = moment().toISOString()
    return payload
  }

  const updateGroup = async () => {
    try {
      const payload = await buildPayload()
      await save('group', group.id, payload)
      onChange && onChange()
      closeDrawer()
      toast({ title: 'Group updated', status: 'success', duration: null, isClosable: true })
    } catch (err) {
      toast({ title: 'Error!', description: err.message, status: 'error', duration: null, isClosable: true })
    }
  }

  const createGroup = async () => {
    // A copy starts with an empty name; a group without one cannot be told apart from the original
    if (template && !data.name?.trim()) {
      toast({ title: 'Error!', description: 'Enter a name for the new group.', status: 'error', duration: null, isClosable: true })
      return
    }
    try {
      const payload = await buildPayload()
      payload.clientId = user.clientId
      await create('group', payload)
      onChange && onChange()
      closeDrawer()
      toast({ title: 'Group created', status: 'success', duration: null, isClosable: true })
    } catch (err) {
      toast({ title: 'Error!', description: err.message, status: 'error', duration: null, isClosable: true })
    }
  }
  
  return (
    <>
      <DrawerBody>
        <GroupForm group={data} onChange={group => setData(group)} />
      </DrawerBody>
      <DrawerFooter>
        {group && (
          <Button colorScheme='brand' borderRadius="30px" me="10px" onClick={() => updateGroup()}>
            Save
          </Button>
        )}
        {!group && (
          <Button colorScheme='brand' borderRadius="30px" me="10px" onClick={() => createGroup()}>
            {template ? 'Create copy' : 'Create'}
          </Button>
        )}
      </DrawerFooter>
    </>
  );
}